import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/AuditLog.css';

function AuditLog() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filter states
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    userId: '',
    actions: [],
    limit: 1000
  });

  const actionOptions = [
    { value: 'LOGIN', label: 'Login' },
    { value: 'CREATE_REPORT', label: 'Create Report' },
    { value: 'EDIT_REPORT', label: 'Edit Report' },
    { value: 'READ_REPORT', label: 'Read Report' },
    { value: 'DELETE_REPORT', label: 'Delete Report' }
  ];

  useEffect(() => {
    loadUsers();
    loadLogs();
  }, []);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setUsers(data || []);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();

      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.actions.length > 0) {
        filters.actions.forEach(action => params.append('actions', action));
      }
      params.append('limit', filters.limit);

      const res = await fetch(`/api/admin/audit-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to fetch audit logs');

      const data = await res.json();
      setLogs(data.logs || []);
    } catch (err) {
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleActionToggle = (action) => {
    setFilters(prev => ({
      ...prev,
      actions: prev.actions.includes(action)
        ? prev.actions.filter(a => a !== action)
        : [...prev.actions, action]
    }));
  };

  const handleApplyFilters = () => {
    loadLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      userId: '',
      actions: [],
      limit: 1000
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('no-NO');
  };

  const getActionBadgeClass = (action) => {
    const classes = {
      'LOGIN': 'badge-login',
      'CREATE_REPORT': 'badge-create',
      'EDIT_REPORT': 'badge-edit',
      'READ_REPORT': 'badge-read',
      'DELETE_REPORT': 'badge-delete'
    };
    return classes[action] || 'badge-default';
  };

  const getActionLabel = (action) => {
    const labels = {
      'LOGIN': 'Login',
      'CREATE_REPORT': 'Created',
      'EDIT_REPORT': 'Edited',
      'READ_REPORT': 'Viewed',
      'DELETE_REPORT': 'Deleted'
    };
    return labels[action] || action;
  };

  return (
    <div className="audit-log-container">
      <div className="audit-log-header">
        <h1>Audit Log</h1>
        <button onClick={() => navigate('/admin')} className="back-button">
          ← Back to Admin
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters-section">
        <h2>Filters</h2>
        <div className="filters-grid">
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="datetime-local"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>To Date</label>
            <input
              type="datetime-local"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>User</label>
            <select
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.full_name} ({user.username})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Activity Type</label>
            <div className="checkbox-group">
              {actionOptions.map(option => (
                <label key={option.value}>
                  <input
                    type="checkbox"
                    checked={filters.actions.includes(option.value)}
                    onChange={() => handleActionToggle(option.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="filters-actions">
          <button onClick={handleApplyFilters} className="btn-primary">
            Apply Filters
          </button>
          <button onClick={handleClearFilters} className="btn-secondary">
            Clear Filters
          </button>
        </div>
      </div>

      <div className="logs-section">
        <h2>Activity Log ({logs.length} entries)</h2>

        {loading ? (
          <p className="loading">Loading...</p>
        ) : logs.length === 0 ? (
          <p className="no-data">No audit logs found</p>
        ) : (
          <div className="logs-table-wrapper">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Activity</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td className="timestamp">{formatDate(log.timestamp)}</td>
                    <td className="username">{log.full_name}<br/><small>{log.username}</small></td>
                    <td>
                      <span className={`badge ${getActionBadgeClass(log.action)}`}>
                        {getActionLabel(log.action)}
                      </span>
                    </td>
                    <td className="details">
                      {log.report_id ? (
                        <span>Rapport #{log.report_id}{log.report_info ? ` - ${log.report_info}` : ''}</span>
                      ) : log.report_info ? (
                        <span>{log.report_info}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditLog;
